import { flatMap } from 'lodash';

import type Item from 'bundles/author-course/models/Item';
// eslint-disable-next-line import/no-duplicates
import type Lesson from 'bundles/author-course/models/Lesson';
// eslint-disable-next-line import/no-duplicates
import type { ParentIdsForItemId } from 'bundles/author-course/models/Lesson';
import Module from 'bundles/author-course/models/Module';
import type { AuthoringBranchChangeScope } from 'bundles/author-course/types/types';
import { hasValidationErrors } from 'bundles/author-course/utils/CourseMaterialValidationUtils';
import LetterGradeScheme from 'bundles/authoring-grading-formula/models/LetterGradeScheme';
import createGradePolicyModel from 'bundles/authoring-grading-formula/utils/createGradePolicyModel';
import createGradedAssignmentGroupModels from 'bundles/authoring-grading-formula/utils/createGradedAssignmentGroupModels';
// TODO: This is suspicious, we should replace with an author-facing learning objectives definition.
import type { OnDemandLearningObjectivesV2 } from 'bundles/learner-learning-objectives/components/data/__generated__/OnDemandLearningObjectivesV2';

type ItemToLearningObjectives = Record<Item['id'], Array<OnDemandLearningObjectivesV2['id']>>;
// TODO: Typing for this model is not finished.
class CourseMaterial {
  declare modules: Array<Module>;

  declare context: any;

  declare itemToLearningObjectives: ItemToLearningObjectives;

  declare learningObjectives: Array<OnDemandLearningObjectivesV2['id']>;

  declare changeScope: AuthoringBranchChangeScope;

  declare gradePolicy: any;

  declare gradingParameters: {
    letterGradeScheme: any;
    gradedAssignmentGroups: any;
    anonymouslyGradedItems: any;
    displayCurrentGrade: boolean;
  };

  declare elements: any;

  declare elementsRequiredForPassing: any;

  constructor({
    courseMaterial,
    authoringAtoms,
    authoringAtomRelations,
    changeScope,
    drafts,
    courseSlug,
    courseId,
    branchId,
    isBranchLaunched,
    isBreakingChangeAllowed,
    isBranchPrivate,
  }: $TSFixMe) {
    // Setting a context object to pass down in all models
    const context = {
      changeScope,
      courseSlug,
      courseId,
      branchId,
      isBranchLaunched,
      isBranchPrivate,
      isBreakingChangeAllowed,
      hasCourseValidationErrors: hasValidationErrors(courseMaterial),
      itemLocks: courseMaterial.itemLocks,
      premiumItemLocks: courseMaterial.premiumItemLocks,
    };

    this.context = context;
    this.modules = courseMaterial.elements.map(
      (module: $TSFixMe) =>
        new Module(Object.assign({ context, drafts, authoringAtoms, authoringAtomRelations }, module))
    );
    this.itemToLearningObjectives = courseMaterial.itemToLearningObjectives || {};
    this.learningObjectives = courseMaterial.learningObjectives || [];
    this.changeScope = changeScope;
    this.gradePolicy = createGradePolicyModel(courseMaterial.gradePolicy);

    const { letterGradeScheme, gradedAssignmentGroups, anonymouslyGradedItems, displayCurrentGrade } =
      courseMaterial.gradingParameters || {};
    this.gradingParameters = {
      letterGradeScheme: new LetterGradeScheme(letterGradeScheme),
      gradedAssignmentGroups: createGradedAssignmentGroupModels(gradedAssignmentGroups),
      anonymouslyGradedItems,
      displayCurrentGrade,
    };

    // Keeping elements in the model for backward compatibility
    this.elements = courseMaterial.elements;

    this.elementsRequiredForPassing = courseMaterial.elementsRequiredForPassing;
  }

  get items(): Item[] {
    // [fe-tech-debt] just use builtin flatmap when we are using node > 10
    return flatMap(this.lessons, (lesson) => lesson.items);
  }

  get lessons(): Lesson[] {
    // [fe-tech-debt] just use builtin flatmap when we are using node > 10
    return flatMap(this.modules, (module) => module.lessons);
  }

  get gradableItems() {
    return this.items.filter((item) => item.isGraded);
  }

  get defaultCourseSchedule() {
    return this.modules.map((module) => {
      return {
        moduleIds: [module.id],
        moduleNames: [module.name],
        itemIds: module.items.map((item) => item.id),
      };
    });
  }

  getParentIdsForItems(itemIds: Array<string>) {
    return this.modules.reduce((prevParentIds, module) => {
      return Object.assign(prevParentIds, module.getParentIdsForItems(itemIds));
    }, {} as ParentIdsForItemId);
  }

  updateDrafts(drafts: $TSFixMe, draftsWithParentIds: $TSFixMe) {
    if (!drafts) {
      return;
    }

    this.modules.forEach((module) => {
      module.updateDrafts(drafts, draftsWithParentIds);
    });
  }
}

export default CourseMaterial;

export type CourseMaterialType = typeof CourseMaterial;
